import type { Assistant, AssistantVoice, AssistantVoiceOption, ChunkPlan } from '@/api/assistants/assistants.types';
import {
  HelpCircle,
  Volume2,
  Mic2,
  Hash,
  Scissors,
  Timer,
  Radio,
  Wand2,
  AlertCircle,
  List,
  Settings2,
  Sliders,
} from 'lucide-react';
import { Combobox } from '@/components/ui/combobox';
import { NumberInput } from '@/components/ui/number-input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { SwitchWithLabel } from '@/components/ui/switch-with-label';
import { SliderWithInput } from '@/components/ui/slider-with-input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { BadgeMultiSelect } from "@/components/ui/badge-multi-select";
import { Skeleton } from "@/components/ui/skeleton";
import { useState, useEffect } from 'react';
import { getAssistantVoices } from '@/api/assistants/assistants.api';

const CUSTOM_MODE_EMPTY_URL_PLACEHOLDER = '__CUSTOM_SOUND_URL_PENDING__';

const isValidHttpUrl = (string: string): boolean => {
  if (!string) return true; // Technically, an empty string isn't a URL, but we handle it specifically
  try {
    const url = new URL(string);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_) {
    return false;
  }
};

interface Option {
  value: string;
  label: string;
}

interface VoiceTabProps {
  assistant: Omit<Assistant, 'name'>;
  onUpdate: (updates: Partial<Assistant>) => void;
}

export function VoiceTab({ assistant, onUpdate }: VoiceTabProps) {
  // Ensure voice config exists with defaults
  const voice = assistant?.voice || {};
  const chunkPlan = voice?.chunkPlan || { minCharacters: 0, punctuationBoundaries: [] };

  const [voices, setVoices] = useState<AssistantVoiceOption[]>([]);
  const [voiceError, setVoiceError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [customSoundUrlError, setCustomSoundUrlError] = useState<string>('');

  // State for custom background sound
  const initialIsCustomSound = !!(assistant.backgroundSound && assistant.backgroundSound !== 'off' && assistant.backgroundSound !== 'office') || assistant.backgroundSound === CUSTOM_MODE_EMPTY_URL_PLACEHOLDER;
  const [isCustomSoundSelected, setIsCustomSoundSelected] = useState<boolean>(initialIsCustomSound);
  const [customSoundUrl, setCustomSoundUrl] = useState<string>(initialIsCustomSound && assistant.backgroundSound !== CUSTOM_MODE_EMPTY_URL_PLACEHOLDER ? assistant.backgroundSound! : '');

  useEffect(() => {
    const fetchVoices = async () => {
      setIsLoading(true);
      const result = await getAssistantVoices();
    //   console.log("Voice API Response:", result);
      if (result.error) {
        setVoiceError(result.error);
      } else if (result.data) {
        setVoices(result.data);
        // console.log("Voices set:", result.data);
      }
      setIsLoading(false);
    };
    fetchVoices();
  }, []);

  // Effect to sync custom sound states with prop changes
  useEffect(() => {
    const isActuallyCustomProp = !!(assistant.backgroundSound && assistant.backgroundSound !== 'off' && assistant.backgroundSound !== 'office') || assistant.backgroundSound === CUSTOM_MODE_EMPTY_URL_PLACEHOLDER;
    setIsCustomSoundSelected(isActuallyCustomProp);

    const currentUrl = (isActuallyCustomProp && assistant.backgroundSound !== CUSTOM_MODE_EMPTY_URL_PLACEHOLDER) ? assistant.backgroundSound! : '';
    setCustomSoundUrl(currentUrl);

    if (currentUrl && !isValidHttpUrl(currentUrl)) {
        setCustomSoundUrlError('The current custom sound URL is invalid. Please provide a valid HTTP/HTTPS URL.');
    } else {
        setCustomSoundUrlError('');
    }
  }, [assistant.backgroundSound]);

  const handleUpdate = (
    key: string,
    value: AssistantVoice[keyof AssistantVoice] | ChunkPlan[keyof ChunkPlan]
  ) => {
    // Handle nested updates
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      const parentKey = parent as keyof AssistantVoice;
      const parentValue = assistant.voice?.[parentKey] as { [key: string]: any };

      onUpdate({
        voice: {
          ...assistant.voice,
          [parentKey]: {
            ...(parentValue || {}),
            [child]: value
          }
        }
      } as Partial<Assistant>);
    } else {
      const voiceKey = key as keyof AssistantVoice;
      onUpdate({
        voice: {
          ...assistant.voice,
          [voiceKey]: value
        }
      } as Partial<Assistant>);
    }
  };

  const handleRootUpdate = (key: keyof Assistant, value: Assistant[keyof Assistant]) => {
    onUpdate({
      [key]: value
    } as Partial<Assistant>);
  };



  const punctuationOptions: Option[] = [
    { value: '.', label: 'Period (.)' },
    { value: ',', label: 'Comma (,)' },
    { value: '!', label: 'Exclamation (!)' },
    { value: '?', label: 'Question Mark (?)' },
    { value: ':', label: 'Colon (:)' },
    { value: ')', label: 'Right Parenthesis ()' },
    { value: '-', label: 'Hyphen (-)' },
    { value: '|', label: 'Vertical Bar (|)' },
    { value: '||', label: 'Double Vertical Bar (||)' },
  ];

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Left Column - Main Configuration */}
      <div className="col-span-8 space-y-6">
        {/* Voice Configuration Card */}
        <Card className="shadow-sm">
          <CardHeader className="border-b bg-muted/20">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <Volume2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 space-y-1">
                <CardTitle>Voice Configuration</CardTitle>
                <CardDescription>Select voice and audio settings</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
          {/* Voice Selection */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Mic2 className="h-4 w-4 text-primary" />
              <Label>Voice</Label>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <p>Select the voice for your assistant</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {voiceError ? (
              <div className="flex items-center text-destructive gap-2">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{voiceError}</span>
              </div>
            ) : isLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Combobox
                options={voices.map((voiceOption) => ({
                  value: voiceOption.name,
                  label: voiceOption.name
                }))}
                value={voice.voiceId || ''}
                onValueChange={(value) => {
                  const selectedVoice = voices.find(v => v.name === value);
                  onUpdate({
                    voice: {
                      ...voice,
                      voiceId: value,
                      language: selectedVoice?.languageCode || voice.language
                    }
                  });
                }}
                placeholder="Select a voice..."
                searchPlaceholder="Search voices..."
                emptyMessage="No voice found."
              />
            )}
          </div>

          {/* Background Sound */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Radio className="h-4 w-4 text-primary" />
              <Label>Background Sound</Label>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <p>This is the background sound in the call. Default for phone calls is 'office' and default for web calls is 'off'.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Combobox
              options={[
                { value: 'off', label: 'Off' },
                { value: 'office', label: 'Office' },
                { value: 'custom', label: 'Custom' }
              ]}
              value={isCustomSoundSelected ? 'custom' : (assistant.backgroundSound || 'off')}
              onValueChange={(value) => {
                if (value === 'custom') {
                  setIsCustomSoundSelected(true);
                  onUpdate({ backgroundSound: customSoundUrl || CUSTOM_MODE_EMPTY_URL_PLACEHOLDER });
                } else {
                  setIsCustomSoundSelected(false);
                  setCustomSoundUrl('');
                  onUpdate({ backgroundSound: value });
                }
              }}
              placeholder="Select background sound..."
              searchPlaceholder="Search options..."
              emptyMessage="No option found."
            />
            {isCustomSoundSelected && (
              <Input
                type="text"
                placeholder="You can use a public URL of an audio file."
                value={customSoundUrl}
                onChange={(e) => {
                  const newUrl = e.target.value;
                  setCustomSoundUrl(newUrl);
                  if (!newUrl) {
                    setCustomSoundUrlError('');
                    onUpdate({ backgroundSound: CUSTOM_MODE_EMPTY_URL_PLACEHOLDER });
                  } else if (isValidHttpUrl(newUrl)) {
                    setCustomSoundUrlError('');
                    onUpdate({ backgroundSound: newUrl });
                  } else {
                    setCustomSoundUrlError('Please enter a valid public HTTP/HTTPS URL.');
                  }
                }}
                className="h-9"
              />
            )}
            {isCustomSoundSelected && customSoundUrlError && (
              <p className="text-sm text-destructive">{customSoundUrlError}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Voice Chunking Card */}
      <Card className="shadow-sm">
        <CardHeader className="border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Scissors className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <CardTitle>Voice Chunking</CardTitle>
              <CardDescription>Control how text is divided for voice generation</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Input Min Characters */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-primary" />
              <Label>Input Min Characters</Label>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <p>This is the minimum number of characters that will be passed to the voice provider. This helps decides the minimum chunk size that is sent to the voice provider for the voice generation as the LLM tokens are streaming in.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <NumberInput
              value={chunkPlan.minCharacters ?? 0}
              onValueChange={(value) => handleUpdate('chunkPlan.minCharacters', value || 0)}
              min={0}
              max={1000}
              stepper={10}
              decimalScale={0}
            />
          </div>

          {/* Punctuation Boundaries */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <List className="h-4 w-4 text-primary" />
              <Label>Punctuation Boundaries</Label>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <p>Select punctuation marks that will act as voice chunk delimiters</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <BadgeMultiSelect
              options={punctuationOptions}
              value={chunkPlan.punctuationBoundaries || []}
              onChange={(value) => handleUpdate('chunkPlan.punctuationBoundaries', value)}
              placeholder="Select punctuation marks..."
            />
          </div>
        </CardContent>
      </Card>
    </div>

    {/* Right Column - Parameters */}
    <div className="col-span-4 space-y-6">
      {/* Voice Tuning Card */}
      <Card className="shadow-sm">
        <CardHeader className="border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Sliders className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <CardTitle>Voice Tuning</CardTitle>
              <CardDescription>Fine-tune voice quality parameters</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Sliders Section */}
          <div className="space-y-2">
            {/* Stability Slider */}
            <SliderWithInput
              label="Stability"
              icon={Wand2}
              tooltip="Increasing stability will make the voice more consistent between re-generations, but it can also make it sounds a bit monotone. On longer text fragments we recommend lowering this value."
              value={voice.stability || 0}
              onChange={(value) => handleUpdate('stability', value)}
              min={0}
              max={1}
              step={0.1}
              leftLabel="More Variable"
              rightLabel="More Stable"
              leftTooltip="Increasing variability can make speech more expressive with output varying between re-generations. It can also lead to instabilities."
              rightTooltip="Increasing stability will make the voice more consistent between re-generations, but it can also make it sounds a bit monotone. On longer text fragments we recommend lowering this value."
              formatValue={(v) => v.toFixed(1)}
              decimalScale={1}
              inputWidth={80}
            />

            {/* Clarity + Similarity Slider */}
            <SliderWithInput
              label="Clarity + Similarity"
              icon={Timer}
              tooltip="Adjust the balance between clarity and similarity in the generated speech."
              value={voice.similarityBoost || 0}
              onChange={(value) => handleUpdate('similarityBoost', value)}
              min={0}
              max={1}
              step={0.1}
              leftLabel="Low"
              rightLabel="High"
              leftTooltip="Low values are recommended if background artifacts are present in generated speech."
              rightTooltip="High enhancement boosts overall voice clarity and target speaker similarity. Very high values can cause artifacts, so adjusting this setting to find the optimal value is encouraged."
              formatValue={(v) => v.toFixed(1)}
              decimalScale={1}
              inputWidth={80}
            />

            {/* Speed Slider */}
            <SliderWithInput
              label="Speed"
              icon={Timer}
              tooltip="Adjust the speed of the generated speech."
              value={voice.speed || 1}
              onChange={(value) => handleUpdate('speed', value)}
              min={0.7}
              max={1.2}
              step={0.1}
              leftLabel="Slower"
              rightLabel="Faster"
              leftTooltip="Slower speech."
              rightTooltip="Faster speech."
              formatValue={(v) => v.toFixed(1)}
              decimalScale={1}
              inputWidth={80}
            />

            {/* Style Exaggeration Slider */}
            <SliderWithInput
              label="Style Exaggeration"
              icon={Wand2}
              tooltip="Control the level of style exaggeration in the generated speech."
              value={voice.style || 0}
              onChange={(value) => handleUpdate('style', value)}
              min={0}
              max={1}
              step={0.1}
              leftLabel="None (Fastest)"
              rightLabel="Exaggerated"
              rightTooltip="High values are recommended if the style of the speech should be exaggerated compared to the uploaded audio. Higher values can lead to more instability in the generated speech. Setting this to 0.0 will greatly increase generation speed and is the default setting."
              formatValue={(v) => v.toFixed(1)}
              decimalScale={1}
              inputWidth={80}
            />

            {/* Optimize Streaming Latency Slider */}
            <SliderWithInput
              label="Optimize Streaming Latency"
              icon={Timer}
              tooltip="Adjust the balance between streaming latency and quality."
              value={voice.optimizeStreamingLatency || 1}
              onChange={(value) => handleUpdate('optimizeStreamingLatency', value)}
              min={1}
              max={4}
              step={1}
              leftLabel="More Latency"
              rightLabel="Less Latency"
              formatValue={(v) => v.toString()}
              inputWidth={80}
            />
          </div>
        </CardContent>
      </Card>

      {/* Voice Features Card */}
      <Card className="shadow-sm">
        <CardHeader className="border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Settings2 className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <CardTitle>Voice Features</CardTitle>
              <CardDescription>Enable advanced voice capabilities</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Switches Section */}
          <div className="space-y-4">
            {/* Filler Injection */}
            <SwitchWithLabel
              label="Filler Injection Enabled"
              icon={Wand2}
              subline="This determines whether fillers are injected into the Model output before inputting it into the Voice provider."
              checked={voice.fillerInjectionEnabled || false}
              onCheckedChange={(checked) => handleUpdate('fillerInjectionEnabled', checked)}
            />

            {/* Backchanneling */}
            <SwitchWithLabel
              label="Backchanneling Enabled"
              icon={Mic2}
              subline="Make the bot say words like 'mhmm', 'ya' etc. while listening to make the conversation sounds natural. Default disabled"
              checked={assistant.backchannelingEnabled || false}
              onCheckedChange={(checked) => handleRootUpdate('backchannelingEnabled', checked)}
            />


            {/* Use Speaker Boost */}
            <SwitchWithLabel
              label="Use Speaker Boost"
              icon={Radio}
              subline="Boost the similarity of the synthesized speech and the voice at the cost of some generation speed."
              checked={voice.useSpeakerBoost || false}
              onCheckedChange={(checked) => handleUpdate('useSpeakerBoost', checked)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
  );
}
